import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
//Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useUserMarketing from '@/utils/hooks/swr/useUserMarketing';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { ErrorCustom } from '@/utils/client';
//Utils
import { put } from '@/utils/client';
import { useSignUpStore } from '@/utils/zustand/providers/sign-up-provider';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';

import { SIGN_UP } from '@/lib/routes/privateRoutes';
import type {
    CompanyPeopleCountOptionType,
    HearAboutUsOptionType,
    IsAgencyDetailOptionType,
    PrimaryGoalOptionType,
    SocialMediaToolOptionType,
    SocialProfileManagedOptionType,
    TeamSizeOptionType
} from '@/lib/signup/userInfos/marketingOptions';
// Types
import type { SubStepType } from '@/lib/signup/userInfos/subSteps';
//Lib
import { subSteps } from '@/lib/signup/userInfos/subSteps';

//Components
import Button from '@/components/base/Button';
import InfoBanner from '@/components/base/InfoBanner';
import StickyFooter from '@/components/pages/signup/StickyFooter';

// Endpoints
import { USER_MARKETING, USER_PROFILE_INFOS } from '@/routes/apiEndpoints';

dayjs.extend(timezone);

export type UserInfosFormType = {
    first_name: string;
    last_name: string;
    timezone: string;
    primary_goal: PrimaryGoalOptionType['value'];
    hear_about_us: HearAboutUsOptionType['value'];
    hear_about_us_detail: string;
    social_profiles_managed: SocialProfileManagedOptionType['value'];
    team_size: TeamSizeOptionType['value'];
    social_media_tool: SocialMediaToolOptionType['value'];
    social_media_tool_detail: string;
    company: string;
    company_people_count: CompanyPeopleCountOptionType['value'];
    is_agency: 'yes' | 'no';
    is_agency_detail: IsAgencyDetailOptionType['value'];
};

type UserInfosFormProps = {
    currentSubStep: SubStepType;
    setCurrentSubStep: (arg: SubStepType) => void;
};

const UserInfosForm = ({ currentSubStep, setCurrentSubStep }: UserInfosFormProps) => {
    const router = useRouter();
    const { t } = useTranslation('signup_funnel');
    const { t_error } = useErrorTranslation();
    const [globalError, setGlobalError, resetGlobalError] = useStateWithReset(null);
    const { updateCurrentStep } = useSignUpStore((state) => state);
    const { user, mutateUser } = useUser();
    const { userMarket, mutateUserMarket } = useUserMarketing();

    const formMethods = useForm<UserInfosFormType>({
        defaultValues: {
            first_name: user.first_name,
            last_name: user.last_name,
            timezone: dayjs.tz.guess(),
            primary_goal: userMarket.primary_goal,
            hear_about_us: userMarket.hear_about_us,
            hear_about_us_detail: userMarket.hear_about_us_detail,
            social_profiles_managed: userMarket.social_profiles_managed,
            team_size: userMarket.team_size,
            social_media_tool: userMarket.social_media_tool,
            social_media_tool_detail: userMarket.social_media_tool_detail,
            company: userMarket.company,
            company_people_count: userMarket?.company_people_count,
            is_agency:
                userMarket.is_agency !== null ? (userMarket?.is_agency ? 'yes' : 'no') : null,
            is_agency_detail: userMarket.is_agency_detail
        }
    });

    const {
        setError,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = formMethods;

    /* Function to submit form */
    const onSubmit = async (data: UserInfosFormType) => {
        resetGlobalError();
        const dataMarket = {
            primary_goal: data.primary_goal,
            hear_about_us: data.hear_about_us,
            hear_about_us_detail: data.hear_about_us_detail,
            social_profiles_managed: data.social_profiles_managed,
            team_size: data.team_size,
            social_media_tool: data.social_media_tool,
            social_media_tool_detail: data.social_media_tool_detail,
            company: data.company,
            company_people_count: data.company_people_count,
            is_agency: data.is_agency !== 'no',
            is_agency_detail: data.is_agency_detail
        };

        const dataUser = {
            first_name: data.first_name,
            last_name: data.last_name,
            settings: {
                timezone: data.timezone
            }
        };

        try {
            await Promise.all([put(USER_PROFILE_INFOS, dataUser), put(USER_MARKETING, dataMarket)]);
            await Promise.all([
                mutateUser(),
                mutateUserMarket({ ...userMarket, ...dataMarket }, { revalidate: false })
            ]);

            if (window?.dataLayer && !user?.first_name) {
                window.dataLayer.push({
                    event: 'add_social_profiles'
                });
            }

            // Change current step
            updateCurrentStep('USER_SET_UP', user.id);
            await router.push(SIGN_UP.USER_SET_UP);
        } catch (error) {
            displayErrors(error);
        }
    };

    const displayErrors = (e: ErrorCustom) => {
        // Error code 422 = error in form => fields errors
        if (e.status === 422) {
            const errorsFields = e.response?.data?.errors;
            // Parse error object, get first error and display errors under each field
            if (errorsFields) {
                displaySpecificErrors(errorsFields);
            } else {
                setGlobalError(t('errors:error_occured_submit_data'));
            }
        } else {
            setGlobalError(t('errors:error_occured_submit_data'));
        }
    };

    const displaySpecificErrors = (errorsFields: ErrorCustom['response']['data']['errors']) => {
        const fields = Object.keys(errorsFields);
        fields.forEach((field: keyof UserInfosFormType | 'settings.timezone') => {
            const error_type = errorsFields[field][0];
            // Display error under each field
            if (field === 'settings.timezone') {
                setError('timezone', {
                    type: 'manual',
                    message: t_error('signup_funnel:user_infos.errors.error_timezone_invalid')
                });
            } else {
                setError(field, {
                    type: 'manual',
                    message: t_error(`signup_funnel:user_infos.errors.error_${field}_${error_type}`)
                });
            }
        });
        // Go to the first substep where a fields error is detected
        let firstSubStepWithErrorId: SubStepType['id'] = null;
        subSteps.forEach((subStep) => {
            if (
                !firstSubStepWithErrorId &&
                fields.filter((field) => subStep.fields.includes(field)).length > 0
            ) {
                firstSubStepWithErrorId = subStep.id;
            }
        });
        if (firstSubStepWithErrorId) {
            goToSubStep(firstSubStepWithErrorId);
        }
    };

    const goToSubStep = (subStepId: SubStepType['id']) => {
        setCurrentSubStep(subSteps.find((subStep) => subStep.id === subStepId));
        const layoutScrollableContent = document.getElementById('layout-scrollable-content');
        if (layoutScrollableContent) {
            layoutScrollableContent.scrollTop = 0;
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form className="flex w-full flex-1 flex-col justify-between space-y-8">
                <div className="flex flex-col space-y-8">
                    {globalError ? (
                        <InfoBanner
                            title={t('errors:error')}
                            message={globalError}
                            onClose={() => setGlobalError(null)}
                        />
                    ) : null}
                    <currentSubStep.form />
                </div>
                <StickyFooter
                    wrapperClassName={clsx({
                        'justify-between': currentSubStep.id !== 1,
                        'justify-end': currentSubStep.id === 1
                    })}>
                    {currentSubStep.id !== 1 ? (
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => {
                                goToSubStep(currentSubStep.id - 1);
                            }}
                            className="font-GMedium text-xs text-icoGray-500 underline">
                            {t('commons:back')}
                        </button>
                    ) : null}
                    <Button
                        disabled={!isValid || isSubmitting}
                        isLoading={isSubmitting}
                        type="button"
                        onClick={
                            currentSubStep.id === subSteps.length
                                ? handleSubmit(onSubmit)
                                : () => {
                                      goToSubStep(currentSubStep.id + 1);
                                  }
                        }>
                        {t('commons:next')}
                    </Button>
                </StickyFooter>
            </form>
        </FormProvider>
    );
};

export default UserInfosForm;
